<template>
	<div>
		<div
			v-show="isEditing || !container.filter(a => a.Value).length"
			class="row justify-left q-py-none"
			:style="{ minHeight: '25px' }"
		>
			<div
				v-for="(field, level) in container"
				class="row justify-left q-py-none q-my-none no-wrap items-center"
				:key="'adr_'+level"
			>
				<q-input
					:ref="(el: any) => { inputs[level] = el }"
					:class="[
						(larger ? 'thin-input-noborder-larger' : 'thin-input-noborder'),
						{ 'err-mode-field': useRequired && field.Required && !field.Value }
					]"
					borderless
					debounce="500"
					no-error-icon
					:input-style="`width: ${(field.Value ? field.Value.length : field.Placeholder.length)*(larger ? 8.5 : 7.5)}px;`"
					v-model="field.Value"
					:placeholder="field.Placeholder"
					@click="fillChains"
					@focus="onFieldFocus(level)"
					@blur="onFieldBlur(level)"
					@update:model-value="modelUpdate"
					@keydown.down.exact.prevent="trackArrowDown(1)"
					@keydown.down.shift.exact.prevent="trackArrowDown(3)"
					@keydown.up.exact.prevent="trackArrowUp(1)"
					@keydown.up.shift.exact.prevent="trackArrowUp(3)"
					@keydown.enter.prevent="selectElement(index_selected)"
					@keydown.left="focusPrev($event, level)"
					@keydown.backspace="focusPrev($event, level)"
					@keydown.right="focusNext($event, level)"
					@keydown.esc="hintOpen = false; isEditing = false;"
				/>
				<div
					v-if="level < container.length-1"
				>,&nbsp;</div>
			</div>
			<q-btn
				v-if="isEditing && showClear"
				padding="xs"
				flat
				dense
				color="grey-7"
				:icon="checkIcon"
				size="sm"
				@click.stop.prevent="saveAddressModel"
			/>
			<q-btn
				v-if="isEditing && showClear"
				padding="xs"
				flat
				dense
				color="grey-7"
				icon="arrow_back"
				size="sm"
				@click.stop.prevent="clearAddressModel"
			/>
			<q-btn
				v-if="showDelete"
				padding="xs"
				flat
				dense
				:aria-label="$t('delete_filter')"
				icon="close"
				size="sm"
				tabindex="-1"
				color="grey-8"
				@click.stop.prevent="emitDelete"
			>
				<q-tooltip :delay="300" anchor="top middle" self="center middle">
					{{ $t('remove') }}
				</q-tooltip>
			</q-btn>
		</div>
		<div
			class="row"
			@click.stop.prevent
			@mousedown.stop.prevent
			@mouseup="isEditing = true"
		>
			<div
				v-for="(span, sp_i) in container.filter(a => a.Value)" :key="sp_i"
				:style="{ fontSize: larger ? '14px' : '13px' }"
				class="no-wrap flex items-center"
				v-show="!isEditing"
			>
				{{ span.Value + (sp_i === container.filter(a => a.Value).length - 1 ? '' : `,&nbsp;`) }}
			</div>
			<q-btn
				v-if="!isEditing && container.filter(a => a.Value).length"
				:style="{ marginLeft: '4px' }"
				padding="xs"
				flat
				dense
				color="grey-7"
				:icon="squareEditIcon"
				size="sm"
			/>
			<q-btn
				v-if="showDelete && !isEditing && container.filter(a => a.Value).length"
				padding="xs"
				flat
				dense
				:aria-label="$t('delete_filter')"
				icon="close"
				size="sm"
				tabindex="-1"
				color="grey-8"
				@click="emitDelete"
			>
				<q-tooltip :delay="300" anchor="top middle" self="center middle">
					{{ $t('remove') }}
				</q-tooltip>
			</q-btn>
		</div>
		<q-menu
			v-model="hintOpen"
			no-parent-event
			no-focus
			style="overflow-x: hidden"
		>
			<!-- <recursive-selector  style="max-height: 50vw;"
			:options="options" :take-ids-only="false" :hide-checkboxes="true"
			:allow-multiple="false" :element-type="1" v-model="theModel" /> -->
			<div
				v-if="options && options.length"
				tabindex="0"
				ref="address_menu"
				style="max-height: 50vh; min-height: 50vh; min-width: 200px; max-width: 500px; overflow-x: hidden; overflow-y: auto; padding: 0 6px"
			>
				<div v-for="(el, index) in options"
					:key="'varlst_'+index"
					:ref="(el: any) => { divs[index] = el }"
					:class="['q-py-none var-lst-item', { 'var-lst-subselection': index_selected === index }]"
					@click="selectElement(index)"
				>
					<simple-element
						:element="{ Id: el.Id, Name: el.DisplayName }"
						:elementType="1"
						:filter_string="this.container[currentLevel]?.Value"
					/>
				</div>
			</div>
			<div v-else class="q-mx-auto q-px-sm">
				{{ $t('no_elements_found') }}
				{{ $t('no_items_found') }}
			</div>
		</q-menu>
	</div>
</template>

<script lang="ts">
import {Component, Emit, Prop, Vue, Watch} from 'vue-facing-decorator';
import {auxStore} from '@/store/auxillary';
import {reactive, ref} from 'vue';
import RecursiveSelector from '@/components/common/basic/recursive-selector/RecursiveSelector.vue';
import {
	AddressAutocompleteChain,
	AddressInputComponentOut,
	AddressInputContainer
} from '@/models/interfaces/entity-settings';
import {CommonEntity} from '@/models/interfaces/common';
import VariableList from '@/components/common/basic/VariableList.vue';
import SimpleElement from '@/components/common/basic/data-components/SimpleElement.vue';
import {mdiSquareEditOutline, mdiCheckCircleOutline} from '@quasar/extras/mdi-v7'

function isInViewport(element: any, viewport: any) {
	if (!element || !viewport) {
		return false
	}
	const rect = element.getBoundingClientRect();
	const viewport_rect = viewport.getBoundingClientRect();

	return (
		rect.top >= viewport_rect.top &&
		rect.left >= viewport_rect.left &&
		rect.bottom <= viewport_rect.bottom &&
		rect.right <= viewport_rect.right
	);
}

@Component({
	name: 'AddressSelector',
	components: {RecursiveSelector, VariableList, SimpleElement}
})
export default class AddressSelector extends Vue {
	@Prop({default: {} as CommonEntity})
	modelValue!: CommonEntity
	@Prop({default: false})
	useRequired!: boolean
	@Prop({default: false})
	larger!: boolean
	@Prop({default: false})
	showClear!: boolean
	@Prop({default: false})
	showDelete!: boolean

	@Emit("update:modelValue") emitModelValue(arg: CommonEntity) {
		return arg;
	}

	@Emit("chainSelected") emitChain(arg: any) {
		return arg;
	}

	@Emit("onDeleteClick") emitDelete(arg: any) {
		return arg;
	}

	squareEditIcon = mdiSquareEditOutline
	checkIcon = mdiCheckCircleOutline

	// menu list
	// viewport to check visibility
	viewport: any
	// selection expire time
	timeout: number | undefined = undefined
	// selection index for arrow (up and down) keys
	index_selected = -1
	// dynamic item refs
	divs: any[] = reactive([])
	// dynamic item refs
	inputs: any[] = reactive([])
	// end menu list

	hintOpen: boolean = false
	isEditing: boolean = false

	scrollTargetRef = ref(null)
	store: any | undefined = undefined
	container: AddressInputComponentOut[] = reactive([]);
	options: any[] = reactive([]);
	currentLevel: number = 0
	minLevel: number = 0

	created() {
		this.store = auxStore()
		this.viewport = this.$refs.address_menu
		this.index_selected = -1

		this.initContainer()
	}

	get theModel() {
		return this.modelValue
	}

	clearAddressModel() {
		this.theModel = reactive({
			Id: 0,
			Name: ''
		})
	}

	saveAddressModel() {
		this.isEditing = false
	}

	focusPrev(event: KeyboardEvent, level: number) {
		if ((this.container[level].Value.length && (this.inputs[level].nativeEl.selectionStart <= 0 && this.inputs[level].nativeEl.selectionEnd === 0)) ||
			!this.container[level].Value.length) {
			this.focusInput(Number(level) - 1)
			event.preventDefault();
		}
	}

	focusNext(event: KeyboardEvent, level: number) {
		if ((this.container[level].Value.length && !this.inputs[level].nativeEl.selectionLength && this.inputs[level].nativeEl.selectionStart >= this.container[level].Value.length) ||
			!this.container[level].Value.length) {
			this.focusInput(Number(level) + 1, true)
			event.preventDefault();
		}
	}

	focusInput(level: number, pos_start = false) {
		if (this.inputs[level]) {
			// @ts-ignore
			this.inputs[level].focus();
			if (pos_start) {
				this.inputs[level].nativeEl.selectionStart = 0;
				this.inputs[level].nativeEl.selectionEnd = 0
			} else if (this.container[level] && this.container[level].Value) {
				this.inputs[level].nativeEl.selectionStart = this.container[level].Value.length;
				this.inputs[level].nativeEl.selectionEnd = this.container[level].Value.length;
			}
			// this.hintOpen = false;
		}
	}

	emitAddressChain() {
		let allEmpty = false;
		let requiredEmpty = false;

		console.log('emit chaing')
		const request: AddressInputContainer = {
			Components: [],
			ActiveLevel: this.currentLevel
		}

		for (let a = 0; a < this.container.length; a++) {
			request.Components.push({
				AddressNodeId: this.container[a].AddressNodeId,
				Level: this.container[a].Level,
				Value: allEmpty ? '' : this.container[a].Value
			})
			if (!this.container[a].Value) {
				allEmpty = a > 0 ? allEmpty && true : true;
			}
			if (this.container[a].Required && !this.container[a].Value) {
				requiredEmpty = true;
			}
		}

		this.emitChain({request, correct: allEmpty || !requiredEmpty});
	}

	set theModel(arg: CommonEntity) {
		if (!arg || !arg) {
			return
		}

		this.emitModelValue(arg)
		// set container values
		this.container.forEach(el => el.Value = '')
		arg.Name.split(',').forEach((el, index) => {
			(this.container && this.container[index]) && (this.container[index].Value = el.trim())
		})

		this.hintOpen = false
		this.isEditing = false
	}

	// events
	onFieldFocus(level: number) {
		const changed_level = this.currentLevel != level;

		this.currentLevel = level >= this.minLevel ? level : this.minLevel;
		(changed_level && this.isEditing && this.hintOpen) && this.fillChains();
	}

	onFieldBlur(level: number) {
		(this.currentLevel === level) && (this.currentLevel = this.minLevel)
	}

	modelUpdate(arg: any) {
		this.fillChains()
	}

	trackArrowUp(step: number) {
		// find first visible element from the bottom
		if (this.index_selected < 0) {
			this.index_selected = this.options.length - 1
			for (let i = this.options.length - 1; i >= 0; i--) {
				// @ts-ignore
				if (isInViewport(this.divs[i], this.viewport)) {
					this.index_selected = i
					break;
				}
			}
		} else {
			// decrement if timer has not expired
			(this.index_selected > 0) && (this.index_selected -= step)

			// if not visible the scroll to element
			if (this.index_selected - 3 >= 0) {
				if (this.divs[this.index_selected - 3] &&
					// @ts-ignore
					!isInViewport(this.divs[this.index_selected], this.viewport)) {
					this.divs[this.index_selected - 3].scrollIntoView({behavior: "smooth"})
				}
			} else {
				this.divs[0] && this.divs[0].scrollIntoView({behavior: "smooth"})
			}
		}

		const that = this
		// clear previous timeout
		if (this.timeout) {
			clearTimeout(this.timeout)
			this.timeout = undefined
		}
		this.timeout = setTimeout(() => {
			that.index_selected = -1
			that.timeout = undefined
		}, 2000)
	}

	trackArrowDown(step: number) {
		// find first visible element from the top
		if (this.index_selected < 0) {
			this.index_selected = 0
			for (let i = 0; i < this.options.length; i++) {
				// @ts-ignore
				if (isInViewport(this.divs[i], this.viewport)) {
					this.index_selected = i
					break;
				}
			}
		} else {
			// increment if timer has not expired
			(this.index_selected < this.divs.length) && (this.index_selected += step)

			// if not visible the scroll to element
			if (this.index_selected < this.divs.length) {
				if (this.divs[this.index_selected] &&
					// @ts-ignore
					!isInViewport(this.divs[this.index_selected], this.viewport)) {
					this.divs[this.index_selected].scrollIntoView({behavior: "smooth"})
				}
			} else {
				this.divs[this.divs.length - 1] && this.divs[this.divs.length - 1].scrollIntoView({behavior: "smooth"})
			}
		}

		const that = this
		// clear previous timeout
		if (this.timeout) {
			clearTimeout(this.timeout)
			this.timeout = undefined
		}
		// keep selection for 2s only
		this.timeout = setTimeout(() => {
			that.index_selected = -1
			that.timeout = undefined
		}, 2000)
	}

	// selection methods
	selectElement(input_i: number) {
		if (input_i < 0 || input_i >= this.options.length) {
			return
		}

		this.theModel = this.options[input_i]
		this.hintOpen = false
		this.isEditing = false

		// set starting index for keyboard
		this.index_selected = input_i
		// clear previous timeout
		const that = this
		if (this.timeout) {
			clearTimeout(this.timeout)
			this.timeout = undefined
		}
		// keep selection for 2s only
		this.timeout = setTimeout(() => {
			that.index_selected = -1
			that.timeout = undefined
		}, 2000)
	}

	async fillChains() {
		if (!this.store) {
			return
		}
		const request: AddressInputContainer = {
			Components: this.container.map(el => {
				return {
					AddressNodeId: el.AddressNodeId,
					Level: el.Level,
					Value: el.Level <= this.currentLevel ? el.Value : ""
				}
			}),
			ActiveLevel: this.currentLevel
		}
		this.options = ((await this.store.getAddressHints(request)).data as AddressAutocompleteChain[])
			.map(el => {
				return {
					Id: el.SheetId,
					Name: el.Components.map(c => c.Value).join(', '),
					DisplayName: el.Components.map(c => c.Value + ' (' + c.Count + ')').join(', ')
				}
			});
		// console.log(this.options)
		this.hintOpen = true
		this.isEditing = true

		this.emitAddressChain()
	}

	@Watch('modelValue', {deep: true})
	m_watcher(val: any, val_prev: any) {
		if (val && (!val_prev || !val_prev.Id)) {
			this.initContainer()
		}
	}

	initContainer() {
		if (!this.store) {
			return
		}

		const that = this
		setTimeout(async () => {
			that.container = (await that.store.getAddressContainerStructure()).data as AddressInputComponentOut[];
			if (that.theModel) {
				// set container values
				that.theModel.Name?.split(',').forEach((el, index) => {
					(that.container && that.container[index]) && (that.container[index].Value = el.trim())
				})
				let minx = 0;
				// if (that.container) {
				//   for (let a = 0; a < that.container.length; a++) {
				//     if (that.container[a].Required) {
				//       that.minLevel = a;
				//     }
				//   }
				// }
			}
			this.emitAddressChain()
		}, 10)
	}
}
</script>

<style lang="scss">
.q-btn__content > span {
	text-align: left !important;
}

/* edit fields */

.thin-input-noborder, .thin-input-noborder > * {
	height: 26px !important;
	min-height: 26px !important;

	.q-field__control {
		height: 26px !important;
		min-height: 26px !important;
		font-size: 12.5px;
	}

	.q-field__control:before {
		border: 0 !important;
	}

	.q-field__control:after {
		border: 0 !important;
	}

	.q-field__native {
		min-height: 26px !important;
	}

	.q-field__marginal {
		height: 26px !important;
	}

	.q-field__bottom {
		font-size: 10px !important;
		padding: 5px 0 0 !important;
	}

	.q-field__native {
		padding: 0 !important;
	}

	.q-field {
		padding-top: 4px !important;
		padding-bottom: 18px !important;
	}
}

.thin-input-noborder-larger, .thin-input-noborder-larger > * {
	height: 20px !important;
	min-height: 20px !important;

	.q-field__control {
		height: 20px !important;
		min-height: 20px !important;
		font-size: 14px;
	}

	.q-field__control:before {
		border: 0 !important;
	}

	.q-field__control:after {
		border: 0 !important;
	}

	.q-field__native {
		min-height: 20px !important;
	}

	.q-field__marginal {
		height: 20px !important;
	}

	.q-field__bottom {
		font-size: 10px !important;
		padding: 5px 0 0 !important;
	}

	.q-field__native {
		padding: 0 !important;
	}

	.q-field {
		padding-top: 4px !important;
		padding-bottom: 18px !important;
	}
}

.err-mode-field {
	.q-field__native {
		color: red !important;
	}
}

</style>
